import { Building2, MapPin, User, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import type { ClientResume, SaisieClient } from '../api/clients';
import type { Resultat } from '../api/client';
import { usePiegeFocus } from '../hooks/usePiegeFocus';

/** Habillage commun des champs : une seule liste de classes pour tout le formulaire. */
export const CLASSE_CHAMP =
  'w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent';

export const CLASSE_LIBELLE = 'block text-sm font-medium text-slate-700 mb-1';

interface Proprietes {
  /** Valeurs admises pour le secteur — servies par le serveur (liste fermée). */
  readonly secteurs: readonly string[];
  /** Enregistre la saisie ; le parent décide quoi faire du résultat. */
  onEnregistrer: (saisie: SaisieClient) => Promise<Resultat<ClientResume>>;
  onReussi: (client: ClientResume) => void;
  onFermer: () => void;
}

/** Les champs d'un client, en sections, avec les refus du serveur affichés tels quels. */
export function FormulaireClient({ secteurs, onEnregistrer, onReussi, onFermer }: Proprietes) {
  const [saisie, setSaisie] = useState<SaisieClient>({
    nom: '',
    contact: '',
    email: '',
    telephone: '',
    ville: '',
    siret: '',
    secteur: '',
    fonction: '',
    rue: '',
    codePostal: '',
    notes: '',
  });
  const [erreur, setErreur] = useState<string>();
  const [envoi, setEnvoi] = useState(false);
  const premier = useRef<HTMLInputElement>(null);
  const boite = useRef<HTMLFormElement>(null);

  /**
   * Le formulaire est une fenêtre MODALE : il occupe l'écran, il bloque le fond.
   * Il doit donc s'annoncer comme telle et retenir le focus, comme les trois
   * autres modales du dépôt — sinon la tabulation repart dans la page derrière.
   */
  usePiegeFocus(boite, true);

  // Le premier champ prend le focus : on ouvre la fiche pour saisir, pas pour lire.
  useEffect(() => {
    premier.current?.focus();
  }, []);

  const modifier = (champ: keyof SaisieClient) => (valeur: string) =>
    setSaisie((precedent) => ({ ...precedent, [champ]: valeur }));

  const soumettre = async (evenement: React.FormEvent) => {
    evenement.preventDefault();
    setEnvoi(true);
    setErreur(undefined);

    const resultat = await onEnregistrer(saisie);
    setEnvoi(false);

    if (resultat.ok && resultat.donnees) {
      onReussi(resultat.donnees);
    } else {
      // Le message du serveur nomme le champ fautif : on ne le remplace pas.
      setErreur(resultat.message ?? 'Enregistrement impossible.');
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <form
        ref={boite}
        role="dialog"
        aria-modal="true"
        aria-labelledby="titre-formulaire-client"
        onSubmit={(evenement) => void soumettre(evenement)}
        onKeyDown={(evenement) => {
          // Échap ferme, comme dans les autres modales du dépôt.
          if (evenement.key === 'Escape') onFermer();
        }}
        className="bg-white rounded-xl shadow-2xl w-full max-w-2xl border border-slate-200 flex flex-col max-h-[90vh]"
      >
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50 flex items-start justify-between gap-4 flex-shrink-0 rounded-t-xl">
          <h2
            id="titre-formulaire-client"
            className="text-lg font-semibold text-slate-800 flex items-center gap-2"
          >
            <Building2 className="w-5 h-5 text-red-600" aria-hidden="true" />
            Ajouter un nouveau client
          </h2>
          <button
            type="button"
            onClick={onFermer}
            aria-label="Fermer le formulaire"
            className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50"
          >
            <X className="w-5 h-5" aria-hidden="true" />
          </button>
        </div>

        <div className="p-6 space-y-5 overflow-y-auto">
          {/* Section 1 : entreprise */}
          <section>
            <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">
              <Building2 className="w-4 h-4 text-slate-400" aria-hidden="true" />
              Informations de l’entreprise
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label htmlFor="client-nom" className={CLASSE_LIBELLE}>
                  Raison sociale <span className="text-red-500">*</span>
                </label>
                <input
                  id="client-nom"
                  ref={premier}
                  value={saisie.nom}
                  onChange={(evenement) => modifier('nom')(evenement.target.value)}
                  className={CLASSE_CHAMP}
                  autoComplete="organization"
                  placeholder="Hôtel Le Meridien"
                  required
                />
              </div>
              <div>
                <label htmlFor="client-siret" className={CLASSE_LIBELLE}>
                  SIRET <span className="text-xs text-slate-400">(facultatif)</span>
                </label>
                <input
                  id="client-siret"
                  value={saisie.siret ?? ''}
                  onChange={(evenement) => modifier('siret')(evenement.target.value)}
                  className={CLASSE_CHAMP}
                  inputMode="numeric"
                  placeholder="123 456 789 00012"
                />
              </div>
              <div>
                <label htmlFor="client-secteur" className={CLASSE_LIBELLE}>
                  Secteur d’activité
                </label>
                <select
                  id="client-secteur"
                  value={saisie.secteur ?? ''}
                  onChange={(evenement) => modifier('secteur')(evenement.target.value)}
                  className={CLASSE_CHAMP}
                >
                  <option value="">Sélectionnez…</option>
                  {secteurs.map((secteur) => (
                    <option key={secteur} value={secteur}>
                      {secteur}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </section>

          {/* Section 2 : contact */}
          <section className="pt-4 border-t border-slate-100">
            <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">
              <User className="w-4 h-4 text-slate-400" aria-hidden="true" />
              Contact principal
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="client-contact" className={CLASSE_LIBELLE}>
                  Nom du contact <span className="text-red-500">*</span>
                </label>
                <input
                  id="client-contact"
                  value={saisie.contact}
                  onChange={(evenement) => modifier('contact')(evenement.target.value)}
                  className={CLASSE_CHAMP}
                  autoComplete="name"
                  placeholder="Marie Dupont"
                  required
                />
              </div>
              <div>
                <label htmlFor="client-fonction" className={CLASSE_LIBELLE}>
                  Fonction <span className="text-xs text-slate-400">(facultatif)</span>
                </label>
                <input
                  id="client-fonction"
                  value={saisie.fonction ?? ''}
                  onChange={(evenement) => modifier('fonction')(evenement.target.value)}
                  className={CLASSE_CHAMP}
                  placeholder="Responsable technique"
                />
              </div>
              <div>
                <label htmlFor="client-email" className={CLASSE_LIBELLE}>
                  Courriel <span className="text-red-500">*</span>
                </label>
                <input
                  id="client-email"
                  type="email"
                  value={saisie.email}
                  onChange={(evenement) => modifier('email')(evenement.target.value)}
                  className={CLASSE_CHAMP}
                  autoComplete="email"
                  placeholder="contact@entreprise.fr"
                  required
                />
              </div>
              <div>
                <label htmlFor="client-telephone" className={CLASSE_LIBELLE}>
                  Téléphone <span className="text-xs text-slate-400">(facultatif)</span>
                </label>
                <input
                  id="client-telephone"
                  value={saisie.telephone}
                  onChange={(evenement) => modifier('telephone')(evenement.target.value)}
                  className={CLASSE_CHAMP}
                  autoComplete="tel"
                  placeholder="01 23 45 67 89"
                />
              </div>
            </div>
          </section>

          {/* Section 3 : adresse */}
          <section className="pt-4 border-t border-slate-100">
            <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">
              <MapPin className="w-4 h-4 text-slate-400" aria-hidden="true" />
              Adresse
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-3">
                <label htmlFor="client-rue" className={CLASSE_LIBELLE}>
                  Rue / adresse <span className="text-xs text-slate-400">(facultatif)</span>
                </label>
                <input
                  id="client-rue"
                  value={saisie.rue ?? ''}
                  onChange={(evenement) => modifier('rue')(evenement.target.value)}
                  className={CLASSE_CHAMP}
                  autoComplete="street-address"
                  placeholder="123 avenue de la République"
                />
              </div>
              <div>
                <label htmlFor="client-code-postal" className={CLASSE_LIBELLE}>
                  Code postal
                </label>
                <input
                  id="client-code-postal"
                  value={saisie.codePostal ?? ''}
                  onChange={(evenement) => modifier('codePostal')(evenement.target.value)}
                  className={CLASSE_CHAMP}
                  inputMode="numeric"
                  autoComplete="postal-code"
                  placeholder="75001"
                />
              </div>
              <div className="md:col-span-2">
                <label htmlFor="client-ville" className={CLASSE_LIBELLE}>
                  Ville <span className="text-red-500">*</span>
                </label>
                <input
                  id="client-ville"
                  value={saisie.ville}
                  onChange={(evenement) => modifier('ville')(evenement.target.value)}
                  className={CLASSE_CHAMP}
                  autoComplete="address-level2"
                  placeholder="Paris"
                  required
                />
              </div>
            </div>
          </section>

          {/* Section 4 : notes */}
          <section className="pt-4 border-t border-slate-100">
            <label htmlFor="client-notes" className={CLASSE_LIBELLE}>
              Notes internes <span className="text-xs text-slate-400">(facultatif)</span>
            </label>
            <textarea
              id="client-notes"
              rows={2}
              value={saisie.notes ?? ''}
              onChange={(evenement) => modifier('notes')(evenement.target.value)}
              className={`${CLASSE_CHAMP} resize-none`}
              placeholder="Informations utiles pour les interventions…"
            />
          </section>

          {erreur && (
            <p role="alert" className="text-sm text-red-700">
              {erreur}
            </p>
          )}
        </div>

        <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex flex-col sm:flex-row items-center justify-end gap-3 flex-shrink-0 rounded-b-xl">
          <span className="text-xs text-slate-500 mr-auto">
            Le client n’est visible que de votre compte.
          </span>
          <button
            type="button"
            onClick={onFermer}
            className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-100"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={envoi}
            className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 disabled:opacity-60"
          >
            {envoi ? 'Enregistrement…' : 'Enregistrer le client'}
          </button>
        </div>
      </form>
    </div>
  );
}
